import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTwoFactor } from '../contexts/TwoFactorContext';
import { supabase } from '../lib/supabaseClient';
import TwoFactorModal from './TwoFactorModal';

const PORTAL_RULES: { prefix: string; requiredType: string; loginPath: string }[] = [
  { prefix: '/admin',          requiredType: 'admin',                   loginPath: '/admin/login' },
  { prefix: '/consultant',     requiredType: 'consultant',              loginPath: '/consultant/login' },
  { prefix: '/workday-client', requiredType: 'workday_services_client', loginPath: '/customer/login' },
];

function getPortalRule(pathname: string) {
  return PORTAL_RULES.find(r => pathname.startsWith(r.prefix)) ?? null;
}

function getLoginPath(userType: string | null | undefined): string {
  if (userType === 'consultant') return '/consultant/login';
  if (userType === 'workday_services_client') return '/customer/login';
  return '/admin/login';
}

// Pure render guard — no side effects, no Supabase calls, no navigate().
// Every branch is a deterministic function of settled auth state.
export default function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, userType, signOut, authHydrated } = useAuth();
  const { twoFAState, is2FAEnabled, statusChecked } = useTwoFactor();
  const location = useLocation();
  // accessToken captured via useEffect once all conditions for showing the modal are met.
  const [capturedToken, setCapturedToken] = useState<string | null>(null);

  const rule = getPortalRule(location.pathname);
  const isLightPortal = location.pathname.startsWith('/consultant') || location.pathname.startsWith('/workday-client');

  // Determine whether the 2FA modal needs to appear so the token capture
  // effect has a stable condition to key off.
  const twofaVerified = (user?.user_metadata as Record<string, unknown> | undefined)?.twofa_verified === true;
  const modalNeeded = statusChecked && is2FAEnabled && !twofaVerified;

  // Capture the access token in a useEffect — never in the render body.
  // Only starts the capture when we know the modal will be shown.
  useEffect(() => {
    if (modalNeeded && user && !capturedToken) {
      supabase.auth.getSession().then(({ data }) => {
        if (data.session?.access_token) {
          setCapturedToken(data.session.access_token);
        } else {
          console.warn('[2FA] Could not capture access token for modal — session missing');
        }
      });
    }
    // Reset captured token when user or 2FA state changes so stale tokens
    // are never reused.
    if (!modalNeeded) {
      setCapturedToken(null);
    }
  }, [modalNeeded, user?.id, capturedToken]);

  // ── CONDITION 1: auth not yet hydrated ──────────────────────────────────
  // Never make any routing decision until AuthContext has resolved.
  if (!authHydrated || user === undefined) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isLightPortal ? 'bg-gray-50' : 'bg-gray-900'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]" />
      </div>
    );
  }

  // ── CONDITION 2: no session ──────────────────────────────────────────────
  if (user === null) {
    const loginPath = rule ? rule.loginPath : '/admin/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // ── CONDITION 3: wrong portal role ──────────────────────────────────────
  // Admins may preview consultant/client portals via the ATS testing page,
  // BUT they must still complete 2FA first (enforced below before children render).
  const isAdminPreview = userType === 'admin' && (
    location.pathname.startsWith('/workday-client') ||
    location.pathname.startsWith('/consultant')
  );

  if (rule && userType !== rule.requiredType && !isAdminPreview) {
    const correctLogin = getLoginPath(userType);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-100">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-500 text-sm mb-1">Your account does not have access to this portal.</p>
          <p className="text-gray-400 text-xs mb-6">
            Signed in as: <span className="font-medium text-gray-600">{user.email}</span>
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => { window.location.href = correctLogin; }}
              className="w-full px-4 py-2.5 bg-[#40E0D0] text-white text-sm font-semibold rounded-lg hover:bg-[#35c5b5] transition-colors"
            >
              Go to My Portal
            </button>
            <button
              onClick={async () => {
                await signOut();
                window.location.href = rule ? rule.loginPath : '/admin/login';
              }}
              className="w-full px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── CONDITION 4: 2FA status not yet resolved ─────────────────────────────
  // Block access while twoFAState === 'loading'. This prevents children from
  // flashing before the async DB check completes. Unknown state = blocked.
  if (!statusChecked) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isLightPortal ? 'bg-gray-50' : 'bg-gray-900'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]" />
      </div>
    );
  }

  // ── CONDITION 5: 2FA status lookup errored ───────────────────────────────
  // FAIL CLOSED. Missing profile row, DB error, RLS block, network failure —
  // any of these leave twoFAState === 'error'. Block all access.
  if (twoFAState === 'error') {
    console.warn('[2FA] Access blocked — 2FA status could not be determined for user:', user.id);
    return (
      <div className={`min-h-screen flex items-center justify-center ${isLightPortal ? 'bg-gray-50' : 'bg-gray-900'}`}>
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-100 mx-4">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Security Check Failed</h2>
          <p className="text-gray-500 text-sm mb-6">
            Your account security status could not be verified. Please sign out and try again, or contact support.
          </p>
          <button
            onClick={async () => { await signOut(); }}
            className="w-full px-4 py-2.5 bg-[#40E0D0] text-white text-sm font-semibold rounded-lg hover:bg-[#35c5b5] transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // ── CONDITION 6: 2FA required but not yet verified ───────────────────────
  // This applies to ALL roles including admin preview routes — there is no bypass.
  // Critically: DO NOT redirect to login. The user IS authenticated; they just
  // haven't completed step 2. Redirecting while authenticated causes the login
  // page to see a live session and triggers a SIGNED_OUT loop.
  // Render the modal inline so the session stays intact.
  if (is2FAEnabled && !twofaVerified) {
    // Show spinner while the access token is being fetched in the useEffect.
    if (!capturedToken) {
      return (
        <div className={`min-h-screen flex items-center justify-center ${isLightPortal ? 'bg-gray-50' : 'bg-gray-900'}`}>
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]" />
        </div>
      );
    }

    return (
      <TwoFactorModal
        accessToken={capturedToken}
        onSuccess={() => {
          // TwoFactorModal refreshed the session after the edge function stamped
          // twofa_verified=true. onAuthStateChange fires TOKEN_REFRESHED, which
          // updates user.user_metadata. This component re-renders with
          // twofaVerified=true and falls through to render children naturally.
        }}
        onCancel={async () => {
          await signOut();
        }}
      />
    );
  }

  // ── ALL CONDITIONS MET — render protected content ────────────────────────
  // authHydrated && user !== null && statusChecked && twoFAState resolved &&
  // (is2FAEnabled === false || twofa_verified === true)
  return <>{children}</>;
}
